import { useMemo, useState } from 'react'
import { Box, Button, Card, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useIsTaskModule, useStatusMeta } from '../moduleContext'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import FilterCenterFocusIcon from '@mui/icons-material/FilterCenterFocus'
import { PRIORITY_META, type Assignee, type DummyWorkOrder } from '../dummy'
import { useSelectWorkOrder, useWorkOrderRowMenu } from '../WorkOrdersPage'
import { useOrgSettings } from '../../../hooks/useOrgSettings'

// The Bacsys exclusive — a Gantt-style timeline with swim lanes per technician.
// At a glance: who's overloaded, who's free, what's overdue, what's coming up.

const DAY_COUNT = 14
const DAY_WIDTH = 112
const BAR_ROW_HEIGHT = 32
const BAR_TOP_PADDING = 12
const LANE_MIN_HEIGHT = 180
const LANE_LABEL_WIDTH = 220
const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

export function WorkloadView({ workOrders }: { workOrders: DummyWorkOrder[] }) {
  const isTask = useIsTaskModule()
  const woAbbrev = isTask ? 'Task' : 'WO'
  const [anchor, setAnchor] = useState(() => {
    // Start 2 days before today so overdue items are visible
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    d.setDate(d.getDate() - 2)
    return d
  })

  const rangeStart = useMemo(() => new Date(anchor), [anchor])
  const rangeEnd   = useMemo(() => new Date(rangeStart.getTime() + DAY_COUNT * DAY_MS), [rangeStart])

  const days = useMemo(
    () => Array.from({ length: DAY_COUNT }, (_, i) => new Date(rangeStart.getTime() + i * DAY_MS)),
    [rangeStart],
  )

  // Position the "today" indicator
  const now = new Date()
  const todayOffset = Math.max(0, (now.getTime() - rangeStart.getTime()) / DAY_MS)
  const todayVisible = todayOffset >= 0 && todayOffset <= DAY_COUNT
  const todayX = LANE_LABEL_WIDTH + todayOffset * DAY_WIDTH

  // Build lanes — one per unique assignee from actual work orders
  const lanes = useMemo(() => {
    const assigneeMap = new Map<string, Assignee>()
    for (const w of workOrders) {
      for (const a of w.assignees) {
        if (!assigneeMap.has(a.id)) assigneeMap.set(a.id, a)
      }
    }
    // Also add an "Unassigned" lane for work orders with no assignee
    const unassigned: DummyWorkOrder[] = workOrders.filter(
      (w) => w.assignees.length === 0 &&
        w.dueDate.getTime() >= rangeStart.getTime() - DAY_MS &&
        w.dueDate.getTime() <= rangeEnd.getTime()
    )

    const result = [...assigneeMap.values()].map((assignee) => {
      const items = workOrders.filter(
        (w) =>
          w.assignees.some((a) => a.id === assignee.id) &&
          w.dueDate.getTime() >= rangeStart.getTime() - DAY_MS &&
          w.dueDate.getTime() <= rangeEnd.getTime(),
      )
      const hours = items.reduce((sum, w) => sum + w.estimatedHours, 0)
      const overdue = items.filter((w) => w.dueDate.getTime() < now.getTime() && w.status !== 'closed' && w.status !== 'resolved').length
      return { assignee, items, hours, overdue }
    })

    if (unassigned.length > 0) {
      result.push({
        assignee: { id: 'unassigned', name: 'Unassigned', initials: '??', color: '#94A3B8' },
        items: unassigned,
        hours: unassigned.reduce((sum, w) => sum + w.estimatedHours, 0),
        overdue: unassigned.filter((w) => w.dueDate.getTime() < now.getTime() && w.status !== 'closed' && w.status !== 'resolved').length,
      })
    }

    return result
  }, [workOrders, rangeStart, rangeEnd, now])

  function shift(days: number) {
    const next = new Date(anchor)
    next.setDate(next.getDate() + days)
    setAnchor(next)
  }

  function scrollToToday() {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    d.setDate(d.getDate() - 2)
    setAnchor(d)
  }

  const totalWidth = LANE_LABEL_WIDTH + DAY_COUNT * DAY_WIDTH

  return (
    <Card
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.015em' }}>
              Workload timeline
            </Typography>
            <Chip
              label="Bacsys exclusive"
              size="small"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                height: 18,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                letterSpacing: '0.02em',
              }}
            />
          </Stack>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.25 }}>
            14-day schedule — one lane per technician, colored by priority
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton size="small" onClick={() => shift(-7)} sx={{ color: 'text.secondary' }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            startIcon={<FilterCenterFocusIcon sx={{ fontSize: 15 }} />}
            onClick={scrollToToday}
            sx={{ fontSize: 12, color: 'text.secondary' }}
          >
            Today
          </Button>
          <IconButton size="small" onClick={() => shift(7)} sx={{ color: 'text.secondary' }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Scrollable timeline — owns both axes */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ position: 'relative', minWidth: totalWidth }}>
          {/* Date scale — above everything when scrolling */}
          <Box
            sx={{
              display: 'flex',
              borderBottom: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: 20,
            }}
          >
            <Box sx={{ width: LANE_LABEL_WIDTH, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }} />
            {days.map((d, i) => {
              const isToday = sameDay(d, new Date())
              const isWeekend = d.getDay() === 0 || d.getDay() === 6
              return (
                <Box
                  key={i}
                  sx={{
                    width: DAY_WIDTH, flexShrink: 0,
                    py: 1.25, px: 1.5,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    bgcolor: isWeekend ? (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)' : 'transparent',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: isToday ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {d.toLocaleDateString(undefined, { weekday: 'short' })}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: isToday ? 700 : 500,
                      color: isToday ? 'primary.main' : 'text.primary',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {d.getDate()} {d.toLocaleDateString(undefined, { month: 'short' })}
                  </Typography>
                </Box>
              )
            })}
          </Box>

          {/* Today vertical line */}
          {todayVisible && (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: 0, bottom: 0,
                left: todayX,
                width: 1,
                bgcolor: 'primary.main',
                opacity: 0.5,
                zIndex: 1,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: -4,
                  width: 9, height: 9,
                  borderRadius: 99,
                  bgcolor: 'primary.main',
                },
              }}
            />
          )}

          {/* Lanes */}
          {lanes.map((lane) => {
            const bars = layoutBars(lane.items, rangeStart)
            const maxRow = bars.reduce((m, b) => Math.max(m, b.row), 0)
            const laneHeight = Math.max(LANE_MIN_HEIGHT, BAR_TOP_PADDING + (maxRow + 1) * BAR_ROW_HEIGHT + 14)
            return (
            <Box
              key={lane.assignee.id}
              sx={{
                display: 'flex',
                position: 'relative',
                borderBottom: '1px solid',
                borderColor: 'divider',
                height: laneHeight,
                '&:last-child': { borderBottom: 'none' },
                '&:hover': {
                  bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.02)',
                },
              }}
            >
              {/* Lane label — fixed left column, z-index above bars so they slide under */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  width: LANE_LABEL_WIDTH, flexShrink: 0,
                  px: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  position: 'sticky',
                  left: 0,
                  bgcolor: 'background.paper',
                  zIndex: 10,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.04)',
                }}
              >
                <Box
                  sx={{
                    width: 32, height: 32,
                    borderRadius: 99,
                    bgcolor: lane.assignee.color,
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    letterSpacing: '-0.01em',
                    flexShrink: 0,
                  }}
                >
                  {lane.assignee.initials}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13, fontWeight: 600,
                      letterSpacing: '-0.005em', lineHeight: 1.2,
                    }}
                    noWrap
                  >
                    {lane.assignee.name}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {lane.items.length} {woAbbrev} · {lane.hours}h
                    </Typography>
                    {lane.overdue > 0 && (
                      <Chip
                        label={`${lane.overdue} overdue`}
                        size="small"
                        sx={{
                          height: 16, fontSize: 10, fontWeight: 700,
                          bgcolor: '#FEF2F2', color: '#DC2626',
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>

              {/* Timeline canvas */}
              <Box sx={{ position: 'relative', flex: 1, height: laneHeight, overflow: 'hidden' }}>
                {/* Vertical day gridlines */}
                {days.map((d, i) => {
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6
                  return (
                    <Box
                      key={i}
                      aria-hidden
                      sx={{
                        position: 'absolute',
                        top: 0, bottom: 0,
                        left: i * DAY_WIDTH,
                        width: DAY_WIDTH,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        zIndex: 0,
                        bgcolor: isWeekend ? (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.015)' : 'transparent',
                      }}
                    />
                  )
                })}

                {/* WO bars with smart vertical stacking */}
                {bars.map((bar) => (
                  <TimelineBar key={bar.wo.id} bar={bar} />
                ))}
              </Box>
            </Box>
          )})}
        </Box>
      </Box>
    </Card>
  )
}

// ─── Timeline bar ────────────────────────────────────────────────────────────

interface Bar {
  wo: DummyWorkOrder
  startDay: number      // index in range
  durationDays: number  // fractional days
  row: number           // vertical slot within the lane (0, 1, ...)
}

function TimelineBar({ bar }: { bar: Bar }) {
  const p = PRIORITY_META[bar.wo.priority] ?? { label: 'Medium', color: '#D97706', bg: '#FFFBEB' }
  // Status chip in the hover-card uses the module-aware label so
  // Tasks shows "Completed" for `resolved` and Work Orders keeps
  // "Resolved".
  const stMeta = useStatusMeta(bar.wo.status)
  const overdue = bar.wo.dueDate.getTime() < Date.now() && bar.wo.status !== 'closed' && bar.wo.status !== 'resolved'
  const left   = bar.startDay * DAY_WIDTH + 4
  const width  = Math.max(64, bar.durationDays * DAY_WIDTH - 8)
  const top    = BAR_TOP_PADDING + bar.row * BAR_ROW_HEIGHT
  const height = BAR_ROW_HEIGHT - 4
  const select = useSelectWorkOrder()
  const openRowMenu = useWorkOrderRowMenu()
  const { formatDateShort } = useOrgSettings()

  return (
    <Tooltip
      arrow
      slotProps={{ tooltip: { sx: { bgcolor: 'background.paper', color: 'text.primary', border: '1px solid', borderColor: 'divider',
        boxShadow: '0 8px 24px -4px rgba(0,0,0,0.15)', borderRadius: 2, p: 1.75, maxWidth: 280 } },
        arrow: { sx: { color: 'background.paper', '&::before': { border: '1px solid', borderColor: 'divider' } } } }}
      title={
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, mb: 0.75 }}>{bar.wo.title}</Typography>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: p.color }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: p.color }}>{p.label} priority</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
            <Chip label={stMeta.label} size="small"
              sx={{ height: 18, fontSize: 10, fontWeight: 700,
                bgcolor: `${stMeta.color}18`,
                color: stMeta.color }} />
          </Stack>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            Due {formatDateShort(bar.wo.dueDate)}{bar.wo.estimatedHours > 0 ? ` · ${bar.wo.estimatedHours}h` : ''}
          </Typography>
          {bar.wo.location && <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>{bar.wo.location}</Typography>}
          <SlaTooltipLine wo={bar.wo} />
        </Box>
      }
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        onClick={() => select(bar.wo)}
        onContextMenu={(e) => openRowMenu(e, bar.wo)}
        sx={{
          position: 'absolute',
          top, left, width, height,
          px: 1,
          borderRadius: 1,
          bgcolor: p.bg,
          border: '1px solid',
          borderColor: p.color,
          color: p.color,
          cursor: 'pointer',
          overflow: 'hidden',
          zIndex: 2,
          transition: 'transform 120ms ease, box-shadow 120ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 10px -4px ${p.color}55`,
            zIndex: 5,
          },
          // Strike-through effect for completed
          ...(bar.wo.status === 'resolved' && {
            opacity: 0.55,
            '& .wo-title': { textDecoration: 'line-through' },
          }),
          ...(overdue && {
            boxShadow: `inset 0 0 0 1px ${p.color}`,
          }),
        }}
      >
        <Box sx={{ width: 4, height: 4, borderRadius: 99, bgcolor: p.color, flexShrink: 0 }} />
        <Typography
          className="wo-title"
          sx={{
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: '-0.005em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            minWidth: 0,
          }}
        >
          {bar.wo.title}
        </Typography>
        {overdue && (
          <ScheduleOutlinedIcon sx={{ fontSize: 12, color: '#DC2626', flexShrink: 0 }} />
        )}
      </Stack>
    </Tooltip>
  )
}

// ─── Layout algorithm ────────────────────────────────────────────────────────

function layoutBars(items: DummyWorkOrder[], rangeStart: Date): Bar[] {
  // Place each bar as a fixed-width block at its due date position.
  // This shows workload distribution (when things are due) rather than
  // spanning from creation→due which makes everything overlap.
  const BAR_SPAN = 1.8 // days of visual width per bar

  const bars: Bar[] = items
    .map((wo) => {
      const dueDayFrac = (wo.dueDate.getTime() - rangeStart.getTime()) / DAY_MS
      // Place bar ending at the due date
      const startDay = Math.max(0, dueDayFrac - BAR_SPAN)
      const durationDays = Math.min(BAR_SPAN, dueDayFrac)
      return { wo, startDay, durationDays: Math.max(0.5, durationDays), row: 0 }
    })
    .sort((a, b) => a.startDay - b.startDay)

  // Assign vertical rows so overlapping bars don't collide
  const rowEndX: number[] = []
  bars.forEach((bar) => {
    const barEnd = bar.startDay + bar.durationDays
    let row = 0
    while (row < rowEndX.length && (rowEndX[row] ?? 0) > bar.startDay + 0.1) row++
    bar.row = row
    rowEndX[row] = barEnd
  })
  return bars
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function SlaTooltipLine({ wo }: { wo: DummyWorkOrder }) {
  const raw = wo._raw
  if (!raw.sla_policy_id || raw.status === 'resolved') return null

  const now = Date.now()
  const responseDue = raw.sla_response_due ? new Date(raw.sla_response_due).getTime() : null
  const resolutionDue = raw.sla_resolution_due ? new Date(raw.sla_resolution_due).getTime() : null
  const respondedAt = raw.sla_responded_at ? new Date(raw.sla_responded_at).getTime() : null

  let label = ''
  let breached = false

  if (!respondedAt && responseDue) {
    const diff = responseDue - now
    breached = diff <= 0
    label = breached ? `Response breached ${fmtSla(Math.abs(diff))} ago` : `Respond in ${fmtSla(diff)}`
  } else if (resolutionDue && raw.status !== 'closed') {
    const diff = resolutionDue - now
    breached = diff <= 0
    label = breached ? `Resolution breached ${fmtSla(Math.abs(diff))} ago` : `Resolve in ${fmtSla(diff)}`
  }

  if (!label) return null
  const color = breached ? '#DC2626' : '#D97706'

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75 }}>
      <TimerOutlinedIcon sx={{ fontSize: 12, color }} />
      <Typography sx={{ fontSize: 11, fontWeight: 600, color }}>{label}</Typography>
    </Stack>
  )
}

function fmtSla(ms: number): string {
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ${mins % 60}m`
  return `${Math.floor(hrs / 24)}d ${hrs % 24}h`
}
